import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaEnvelope } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { Subtitle, BodyText, Button, Input, Title } from '../../components/ui';
import Layout from '../../components/Layout';
import { requestPasswordReset } from '../../api/services/customerService';
import { setShowLoading } from '../../redux/systemInfo';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 90vh;
  padding: 20px;
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  margin-top: 20px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
`;

const FormContainerTitle = styled.div`
  width: 100%;
  padding: 20px 0;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.subBackground};
`;

const FormContainerBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5% 10%;
`;

const InputContainer = styled.div`
  width: 100%;
  padding: 5px 5px;
  border: 1px solid #ccc;
  gap: 10px;
  margin-bottom: 30px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primaryBlue};
  cursor: pointer;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  position: absolute;
  top: 30px;
  left: 50px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const StyledTitle = styled(Title)`
  margin: 20px 0 30px 0;
  text-align: center;
`;

const StyledBodyTextBold = styled(BodyText)`
  font-weight: bold;
  text-align: left;
  margin: 0 0 10px 0;
`;

const StyledBodyTextCenter = styled(BodyText)`
  text-align: center;
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const StyledBodyTextSuccess = styled(BodyText)`
  text-align: center;
  margin: 0 0 20px 0;
  line-height: 1.5;
  color: green;
`;

const StyledButton = styled(Button)`
  align-self: center;
  width: 100%;
  padding: 15px;
  margin-top: 20px;
`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState(location.state?.email || '');
  const [emailError, setEmailError] = useState({
    isShow: false,
    msg: '',
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSendResetLink = async () => {
    // Reset error state
    setEmailError({ isShow: false, msg: '' });

    // Validate email
    if (!email) {
      setEmailError({ isShow: true, msg: 'Email is required' });
      return;
    }

    if (!email.includes('@')) {
      setEmailError({
        isShow: true,
        msg: 'Please enter a valid email address',
      });
      return;
    }

    try {
      dispatch(setShowLoading({ showLoading: true }));
      const result = await requestPasswordReset(email);
      dispatch(setShowLoading({ showLoading: false }));

      if (result.is_success) {
        setIsSuccess(true);
      } else {
        setEmailError({
          isShow: true,
          msg: result.message || 'Failed to send reset link',
        });
      }
    } catch (error) {
      dispatch(setShowLoading({ showLoading: false }));
      console.error('Error occurred:', error);
      setEmailError({
        isShow: true,
        msg: 'An error occurred. Please try again.',
      });
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Layout>
      <Container>
        <BackButton onClick={handleBackToLogin}>
          <FaArrowLeft />
          Back to Login
        </BackButton>

        <StyledTitle>Forgot Password?</StyledTitle>

        <Wrapper>
          <FormContainer>
            <FormContainerTitle>
              <Subtitle align='center'>Reset Your Password</Subtitle>
            </FormContainerTitle>
            <FormContainerBody>
              <IconContainer>
                <FaEnvelope size={'40px'} color='#223a7a' />
              </IconContainer>

              {!isSuccess ? (
                <>
                  <StyledBodyTextCenter>
                    Enter your email address below and we&apos;ll send you a
                    link to reset your password.
                  </StyledBodyTextCenter>

                  <StyledBodyTextBold>Email:</StyledBodyTextBold>
                  <InputContainer>
                    <Input
                      type='email'
                      placeholder='Enter your email address'
                      value={email}
                      onChange={setEmail}
                    />
                  </InputContainer>
                  {emailError.isShow ? (
                    <BodyText color={'red'}>{emailError.msg}</BodyText>
                  ) : null}

                  <StyledButton onClick={handleSendResetLink}>
                    <Subtitle color='white'>Send Reset Link</Subtitle>
                  </StyledButton>
                </>
              ) : (
                <>
                  <StyledBodyTextSuccess>
                    Password reset link has been sent to your email address.
                    Please check your inbox and follow the instructions to reset
                    your password.
                  </StyledBodyTextSuccess>

                  <StyledButton onClick={() => setIsSuccess(false)}>
                    <Subtitle color='white'>Send Another Link</Subtitle>
                  </StyledButton>
                </>
              )}
            </FormContainerBody>
          </FormContainer>
        </Wrapper>
      </Container>
    </Layout>
  );
};

export default ForgotPasswordPage;
